import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { Client } from '../types';

interface ClientsContextType {
  clients: Client[];
  loading: boolean;
  addClient: (client: Omit<Client, 'id' | 'createdAt' | 'projects'>) => void;
  updateClient: (client: Client) => void;
  deleteClient: (id: string) => void;
  getClientById: (id: string) => Client | undefined;
  addProjectToClient: (clientId: string, projectId: string) => void;
  removeProjectFromClient: (clientId: string, projectId: string) => void;
  getClientByName: (name: string) => Client | undefined;
  createClientFromProject: (clientData: { name: string; address: string; phone?: string; email?: string; company?: string }, projectId: string) => Client;
  updateClientFromProject: (originalClientName: string, updatedClientData: { name: string; address: string; phone?: string; email?: string; company?: string }) => void;
  fetchClients: () => Promise<void>;
}

const ClientsContext = createContext<ClientsContextType | undefined>(undefined);

export const useClients = () => {
  const context = useContext(ClientsContext);
  if (!context) {
    throw new Error('useClients must be used within a ClientsProvider');
  }
  return context;
};

export const ClientsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch clients from Supabase when authenticated and organization is selected
  useEffect(() => {
    if (isAuthenticated && currentOrganization) {
      fetchClients();
    }
  }, [isAuthenticated, currentOrganization?.id]);

  const fetchClients = async () => {
    if (!currentOrganization) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching clients:', error);
        return;
      }

      if (data) {
        // Transform Supabase data to Client format
        const transformedClients: Client[] = data.map(client => ({
          id: client.id,
          name: client.name,
          email: client.email,
          phone: client.phone,
          address: client.address,
          company: client.company,
          notes: client.notes,
          createdAt: client.created_at,
          projects: [] // Will be populated from projects table
        }));

        setClients(transformedClients);
      }
    } catch (error) {
      console.error('Error in fetchClients:', error);
    } finally {
      setLoading(false);
    }
  };

  const addClient = async (client: Omit<Client, 'id' | 'createdAt' | 'projects'>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    try {
      // Check if client with this email already exists in this organization
      if (client.email) {
        const { data: existingClient } = await supabase
          .from('clients')
          .select('id')
          .eq('email', client.email)
          .eq('organization_id', currentOrganization.id)
          .maybeSingle();

        if (existingClient) {
          throw new Error('A client with this email already exists');
        }
      }

      const { data, error } = await supabase
        .from('clients')
        .insert({
          name: client.name,
          email: client.email,
          phone: client.phone,
          address: client.address,
          company: client.company,
          notes: client.notes,
          organization_id: currentOrganization.id
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding client:', error);
        throw error;
      }

      if (data) {
        const newClient: Client = {
          id: data.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          company: data.company,
          notes: data.notes,
          createdAt: data.created_at,
          projects: []
        };
        setClients(prev => [...prev, newClient]);
      }
    } catch (error) {
      console.error('Error in addClient:', error);
      throw error;
    }
  };

  const updateClient = async (updatedClient: Client) => {
    try {
      const { error } = await supabase
        .from('clients')
        .update({
          name: updatedClient.name,
          email: updatedClient.email,
          phone: updatedClient.phone,
          address: updatedClient.address,
          company: updatedClient.company,
          notes: updatedClient.notes
        })
        .eq('id', updatedClient.id);

      if (error) {
        console.error('Error updating client:', error);
        throw error;
      }

      setClients(prev => 
        prev.map(client => 
          client.id === updatedClient.id ? updatedClient : client
        )
      );
    } catch (error) {
      console.error('Error in updateClient:', error);
      throw error;
    }
  };

  const deleteClient = async (id: string) => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting client:', error);
        throw error;
      }

      setClients(prev => prev.filter(client => client.id !== id));
    } catch (error) {
      console.error('Error in deleteClient:', error);
      throw error;
    }
  };

  const getClientById = (id: string) => {
    return clients.find(client => client.id === id);
  };

  const getClientByName = (name: string) => {
    return clients.find(client => client.name.toLowerCase() === name.toLowerCase());
  };

  const addProjectToClient = (clientId: string, projectId: string) => {
    setClients(prev => 
      prev.map(client => {
        if (client.id === clientId && !client.projects.includes(projectId)) {
          return {
            ...client,
            projects: [...client.projects, projectId]
          };
        }
        return client;
      })
    );
  };

  const removeProjectFromClient = (clientId: string, projectId: string) => {
    setClients(prev => 
      prev.map(client => {
        if (client.id === clientId) {
          return {
            ...client,
            projects: client.projects.filter(id => id !== projectId)
          };
        }
        return client;
      })
    );
  };

  const createClientFromProject = (clientData: { name: string; address: string; phone?: string; email?: string; company?: string }, projectId: string) => {
    // Check if client already exists
    const existingClient = getClientByName(clientData.name);
    if (existingClient) {
      // Add project to existing client and update their information if provided
      const updatedClient = {
        ...existingClient,
        // Update client info with new data if provided
        address: clientData.address || existingClient.address,
        phone: clientData.phone || existingClient.phone,
        email: clientData.email || existingClient.email,
        company: clientData.company || existingClient.company,
        projects: existingClient.projects.includes(projectId) 
          ? existingClient.projects 
          : [...existingClient.projects, projectId]
      };
      
      updateClient(updatedClient);
      
      return updatedClient;
    }

    // Create new client
    const clientToAdd = {
      name: clientData.name,
      address: clientData.address,
      phone: clientData.phone,
      email: clientData.email,
      company: clientData.company,
    };

    addClient(clientToAdd);
    
    // Return a temporary client object for immediate use
    return {
      id: 'temp-' + Date.now(),
      ...clientToAdd,
      createdAt: new Date().toISOString(),
      projects: [projectId]
    };
  };

  const updateClientFromProject = (originalClientName: string, updatedClientData: { name: string; address: string; phone?: string; email?: string; company?: string }) => {
    // Find the client by original name
    const existingClient = getClientByName(originalClientName);
    
    if (existingClient) {
      // Update the existing client with new information
      const updatedClient = {
        ...existingClient,
        name: updatedClientData.name,
        address: updatedClientData.address,
        phone: updatedClientData.phone,
        email: updatedClientData.email,
        company: updatedClientData.company
      };
      
      updateClient(updatedClient);
    } else if (updatedClientData.name && updatedClientData.address) {
      // If client doesn't exist but we have valid data, create a new one
      const clientToAdd = {
        name: updatedClientData.name,
        address: updatedClientData.address,
        phone: updatedClientData.phone,
        email: updatedClientData.email,
        company: updatedClientData.company,
      };
      
      addClient(clientToAdd);
    }
  };

  return (
    <ClientsContext.Provider value={{
      clients,
      loading,
      addClient,
      updateClient,
      deleteClient,
      getClientById,
      addProjectToClient,
      removeProjectFromClient,
      getClientByName,
      createClientFromProject,
      updateClientFromProject,
      fetchClients
    }}>
      {children}
    </ClientsContext.Provider>
  );
};